import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@parcel/ui';
import { ArrowUpRight } from 'lucide-react';
import * as React from 'react';
import type { FieldEvidenceCitation, FieldEvidenceEntry } from '../../lib/field-evidence.js';

// A value stays plain text; a small trailing arrow opens the page that states it.

function isWebUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

function corroboration(count: number): string | null {
  if (count <= 0) return null;
  return `Also stated by ${count} other ${count === 1 ? 'publisher' : 'publishers'}`;
}

function citationText(citation: FieldEvidenceCitation): string {
  return `${citation.source_label}: “${citation.excerpt}”`;
}

function host(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const ICON_CLASS =
  'ml-1 inline-flex h-4 w-4 shrink-0 translate-y-[2px] items-center justify-center rounded text-muted-foreground/70 align-baseline transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The trailing source arrow: one page opens directly, several open a short list. */
export function SourceLink({
  evidence,
  className,
}: {
  evidence: FieldEvidenceEntry | null | undefined;
  className?: string;
}) {
  const descriptionId = React.useId();
  const citations = (evidence?.citations ?? []).filter((c) => isWebUrl(c.source_url));
  if (!evidence || citations.length === 0) return null;
  const also = corroboration(evidence.corroborating_sources);

  if (citations.length === 1) {
    const [citation] = citations as [FieldEvidenceCitation];
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={citation.source_url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Source: ${citation.source_label}`}
              aria-describedby={descriptionId}
              className={cn(ICON_CLASS, className)}
            >
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </a>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="font-medium">{citation.source_label}</div>
            <div className="text-muted-foreground">{host(citation.source_url)}</div>
            {also ? <div className="mt-1 text-muted-foreground">{also}</div> : null}
          </TooltipContent>
        </Tooltip>
        <span id={descriptionId} className="sr-only">
          {citationText(citation)}
          {also ? `. ${also}.` : null}
        </span>
      </TooltipProvider>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${citations.length} sources`}
          className={cn(ICON_CLASS, className)}
        >
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1.5 text-sm">
        <div className="px-2 pb-1 pt-0.5 text-xs text-muted-foreground">
          {`${citations.length} sources`}
        </div>
        <ul>
          {citations.map((citation) => (
            <li key={`${citation.source_url}|${citation.excerpt}`}>
              <a
                href={citation.source_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-start justify-between gap-2 rounded px-2 py-1.5 hover:bg-muted"
              >
                <span className="min-w-0">
                  <span className="block truncate">{citation.excerpt}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {host(citation.source_url)}
                  </span>
                </span>
                <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </a>
            </li>
          ))}
        </ul>
        {also ? <div className="px-2 pb-1 text-xs text-muted-foreground">{also}</div> : null}
      </PopoverContent>
    </Popover>
  );
}

/** A value followed by its source arrow; plain text without one. */
export function CitedValue({
  evidence,
  children,
}: {
  evidence: FieldEvidenceEntry | null | undefined;
  children: React.ReactNode;
}) {
  return (
    <>
      {children}
      <SourceLink evidence={evidence} />
    </>
  );
}
